<template>
  <div class="supplier-page">
    <!-- 顶部导航 -->
    <header class="back-header">
      <div class="back-btn" @click="goBack">←</div>
      <h1>{{ $t('wholesaler.purchase.SupplierList.line6_text') }}</h1>
      <div style="flex: 1;"></div>
    </header>

    <div class="page-content">
      <!-- 搜索栏 -->
      <div class="search-section">
        <input 
          type="text" 
          v-model="searchKeyword"
          :placeholder="$t('wholesaler.purchase.SupplierList.line16_placeholder')"
          class="search-input"
          @input="handleSearch"
        />
        <button class="btn-add" @click="goToAdd">{{ $t('wholesaler.purchase.SupplierList.line20_text') }}</button>
      </div>

      <!-- 供应商列表 -->
      <div v-for="supplier in suppliers" :key="supplier.id" class="info-section">
        <div class="section-header">
          <h3>{{ supplier.name }}</h3>
          <span class="status-tag" :class="supplier.status === 'ACTIVE' ? 'active' : 'inactive'">
            {{ supplier.status === 'ACTIVE' ? $t('wholesaler.purchase.SupplierList.status_active') : $t('wholesaler.purchase.SupplierList.status_inactive') }}
          </span>
        </div>
        <div class="form-item" v-if="supplier.contactPerson">
          <label>{{ $t('wholesaler.purchase.SupplierList.line32_text') }}</label>
          <div class="readonly-value">{{ supplier.contactPerson }}</div>
        </div>
        <div class="form-item" v-if="supplier.phone">
          <label>{{ $t('wholesaler.purchase.SupplierList.line36_text') }}</label>
          <div class="readonly-value">{{ supplier.phone }}</div>
        </div>
        <div class="form-item" v-if="supplier.address">
          <label>{{ $t('wholesaler.purchase.SupplierList.line40_text') }}</label>
          <div class="readonly-value">{{ supplier.address }}</div>
        </div>
        <div class="form-item" v-if="supplier.bankName">
          <label>{{ $t('wholesaler.purchase.SupplierList.line44_text') }}</label>
          <div class="readonly-value">{{ supplier.bankName }} - {{ supplier.bankAccountName }} - {{ supplier.bankAccountNumber }}</div>
        </div>
        <div class="card-actions">
          <button class="btn-edit" @click="goToEdit(supplier)">{{ $t('common.edit') }}</button>
          <button 
            class="btn-toggle" 
            :class="supplier.status === 'ACTIVE' ? 'danger' : 'success'"
            @click="toggleStatus(supplier)"
          >
            {{ supplier.status === 'ACTIVE' ? $t('wholesaler.purchase.SupplierList.action_disable') : $t('wholesaler.purchase.SupplierList.action_enable') }}
          </button>
        </div>
      </div>

      <div v-if="suppliers.length === 0 && !loading" class="empty-state">
        <div class="empty-icon">🏭</div>
        <p>{{ $t('wholesaler.purchase.SupplierList.line61_text') }}</p>
        <button class="btn-add" @click="goToAdd">{{ $t('wholesaler.purchase.SupplierList.line62_text') }}</button>
      </div>
    </div>

    <!-- Toast -->
    <div v-if="showToast" class="toast">{{ toastMessage }}</div>

    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <button class="nav-item" @click="navigateTo('home')">
        <span class="nav-item-icon">🏠</span>
        <span>{{ $t('common.home') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('products')">
        <span class="nav-item-icon">📦</span>
        <span>{{ $t('common.products') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('wholesale')">
        <span class="nav-item-icon">💰</span>
        <span>{{ $t('wholesaler.purchase.SupplierList.line81_text') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('orders')">
        <span class="nav-item-icon">📋</span>
        <span>{{ $t('common.orders') }}</span>
      </button>
      <button class="nav-item" @click="navigateTo('profile')">
        <span class="nav-item-icon">👤</span>
        <span>{{ $t('common.profile') }}</span>
      </button>
    </nav>
  </div>
</template>

<script>
import { getSupplierList, deleteSupplier, toggleSupplierStatus } from '@/api/purchase'
import { getWholesalerId } from '@/utils/wholesaler'

export default {
  name: 'SupplierList',

  data() {
    return {
      suppliers: [],
      searchKeyword: '',
      loading: false,
      showToast: false,
      toastMessage: ''
    }
  },

  computed: {
    wholesalerId() {
      return getWholesalerId()
    }
  },

  created() {
    this.loadSuppliers()
  },

  activated() {
    this.loadSuppliers()
  },

  methods: {
    async loadSuppliers() {
      this.loading = true
      try {
        const res = await getSupplierList(this.wholesalerId, this.searchKeyword)
        this.suppliers = res || []
      } catch (e) {
        console.error('加载供应商失败', e)
        this.showToastMsg(this.$t('wholesaler.purchase.SupplierList.toast_load_failed'))
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.loadSuppliers()
    },

    goToAdd() {
      this.$router.push('/wholesaler/supplier-form')
    },

    goToEdit(supplier) {
      this.$router.push({
        path: '/wholesaler/supplier-form',
        query: {
          id: supplier.id,
          name: supplier.name,
          contactPerson: supplier.contactPerson || '',
          phone: supplier.phone || '',
          address: supplier.address || '',
          remark: supplier.remark || '',
          bankName: supplier.bankName || '',
          bankAccountName: supplier.bankAccountName || '',
          bankAccountNumber: supplier.bankAccountNumber || ''
        }
      })
    },

    async toggleStatus(supplier) {
      const isActive = supplier.status === 'ACTIVE'
      const confirmKey = isActive ? 'confirm_disable' : 'confirm_enable'
      const toastKey = isActive ? 'toast_disabled' : 'toast_enabled'
      const confirmMsg = this.$t(`wholesaler.purchase.SupplierList.${confirmKey}`, { name: supplier.name })
      if (!confirm(confirmMsg)) return
      try {
        await toggleSupplierStatus(supplier.id)
        this.showToastMsg(this.$t(`wholesaler.purchase.SupplierList.${toastKey}`))
        this.loadSuppliers()
      } catch (e) {
        this.showToastMsg(this.$t('wholesaler.purchase.SupplierList.toast_operation_failed'))
      }
    },

    navigateTo(tab) {
      const routes = {
        home: '/wholesaler/home',
        products: '/wholesaler/product',
        wholesale: '/wholesaler/wholesale-price-list',
        orders: '/wholesaler/orders',
        profile: '/my-profile'
      }
      if (routes[tab]) {
        this.$router.push(routes[tab])
      }
    },
    goBack() {
      this.$router.go(-1)
    },

    showToastMsg(message) {
      this.toastMessage = message
      this.showToast = true
      setTimeout(() => { this.showToast = false }, 2500)
    }
  }
}
</script>

<style scoped>
.supplier-page {
  min-height: 100vh;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
  padding-bottom: 70px;
}

/* 底部导航 */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: space-around;
  z-index: 100;
  height: 56px;
}

.nav-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #999;
  font-size: 11px;
  border: none;
  background: none;
  transition: color 0.3s;
}

.nav-item.active {
  color: #667eea;
}

.nav-item-icon {
  font-size: 24px;
  margin-bottom: 2px;
}

.back-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 20px;
}

.back-header h1 {
  flex: 1;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  margin-left: 12px;
}

.page-content {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 40px;
}

.search-section {
  background: #fff;
  padding: 12px;
  margin: 12px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  display: flex;
  gap: 8px;
}

.search-input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  font-size: 13px;
  outline: none;
}

.search-input:focus {
  border-color: #ff6b35;
}

.btn-add {
  background: #ff6b35;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}

.info-section {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  margin: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.section-header h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #333;
}

.status-tag {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.status-tag.active {
  background: #e8f5e9;
  color: #4caf50;
}

.status-tag.inactive {
  background: #f5f5f5;
  color: #999;
}

.form-item {
  margin-bottom: 12px;
}

.form-item label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 4px;
  color: #999;
}

.readonly-value {
  font-size: 13px;
  color: #333;
}

.card-actions {
  display: flex;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid #eee;
  margin-top: 12px;
}

.btn-edit {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ff6b35;
  color: #ff6b35;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  font-size: 13px;
}

.btn-toggle {
  flex: 1;
  padding: 8px 12px;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  font-size: 13px;
}

.btn-toggle.danger {
  color: #f44336;
  border: 1px solid #f44336;
}

.btn-toggle.success {
  color: #4caf50;
  border: 1px solid #4caf50;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-state p {
  color: #999;
  margin-bottom: 16px;
}

.toast {
  position: fixed;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0,0,0,0.8);
  color: #fff;
  padding: 12px 20px;
  border-radius: 20px;
  font-size: 13px;
  z-index: 2000;
}
</style>
